import { CloseButton, Modal } from "@mantine/core";
import * as React from "react";
import type { ReturnSerialInfo, ScannedSerialRow } from "../types";
import { DIALOG_MODAL_PROPS, DialogButton, DialogHeader } from "./dialogs";

interface ExchangeSerialPickerModalProps {
  opened: boolean;
  /** Serial xuất đang chọn "Serial nhập" */
  serial: string | null;
  rows: ScannedSerialRow[];
  /** Serial khách trả của đơn đổi trả */
  returnSerials: ReturnSerialInfo[];
  onClose: () => void;
  /** Mảng rỗng = bỏ chọn, backend tự ghép theo dòng đơn; có thì đúng một serial */
  onConfirm: (serial: string, oldSerials: string[]) => void;
}

const serialKey = (serial: string | null | undefined) => (serial ?? "").trim().toUpperCase();

const SwapIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1F5FAE"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5C6570" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/**
 * Xuất đổi trả: hộp chọn serial khách trả mà một serial xuất thay thế ("Serial nhập"). Một đổi một: mỗi serial xuất chọn
 * đúng một serial trả, serial trả đã chọn cho serial xuất khác trên phiếu thì khoá kèm lý do. Danh sách là serial trả
 * của đơn, backend trả kèm phiếu. Bỏ trống thì backend tự ghép theo "Serial đổi" của dòng đơn rồi theo serial còn trống.
 */
const ExchangeSerialPickerModal = ({ opened, serial, rows, returnSerials, onClose, onConfirm }: ExchangeSerialPickerModalProps) => {
  const radioName = React.useId();
  const [selected, setSelected] = React.useState<string | null>(null);
  // Giữ serial đang mở để nội dung không trống trong lúc hộp đóng dần
  const [shownSerial, setShownSerial] = React.useState<string | null>(null);
  const openKey = opened ? serialKey(serial) || null : null;
  const [lastOpenKey, setLastOpenKey] = React.useState<string | null>(null);
  const currentKey = openKey ?? shownSerial ?? "";
  const row = React.useMemo(
    () => (currentKey ? rows.find((item) => serialKey(item.serial) === currentKey) : undefined),
    [rows, currentKey],
  );
  // Mỗi lần mở (hoặc đổi dòng): chọn sẵn serial đang có trên dòng
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (openKey) {
      setShownSerial(openKey);
      const current = rows.find((item) => serialKey(item.serial) === openKey);
      setSelected(serialKey(current?.exchangeOldSerials?.[0]) || null);
    }
  }

  // Serial trả đã được dòng khác trên phiếu chọn -> serial xuất đang giữ nó
  const holderByOldSerial = React.useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach((item) => {
      const key = serialKey(item.serial);
      if (!key || key === currentKey) return;
      (item.exchangeOldSerials ?? []).forEach((old) => {
        const oldKey = serialKey(old);
        if (oldKey && !map.has(oldKey)) map.set(oldKey, key);
      });
    });
    return map;
  }, [rows, currentKey]);

  // Serial vừa nhập về vừa xuất lại trên phiếu này: không phải đổi serial, khoá
  const reissued = React.useMemo(
    () => new Set(rows.map((item) => serialKey(item.serial)).filter(Boolean)),
    [rows],
  );
  const rowItemCode = serialKey(row?.itemCode);
  // Cùng mã hàng với serial xuất đứng trước (máy đổi máy, thẻ đổi thẻ), khác mã hàng vẫn chọn được
  const options = React.useMemo(() => {
    const seen = new Set<string>();
    const unique = returnSerials.filter((item) => {
      const key = serialKey(item.serial);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return unique.sort((a, b) => Number(serialKey(b.itemCode) === rowItemCode) - Number(serialKey(a.itemCode) === rowItemCode));
  }, [returnSerials, rowItemCode]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onExitTransitionEnd={() => setShownSerial(null)}
      {...DIALOG_MODAL_PROPS}
      size={560}
      yOffset="14dvh"
    >
      <DialogHeader
        icon={<SwapIcon />}
        iconBackground="#E6EEF8"
        title={`Serial nhập cho ${currentKey}`}
        description={[row?.itemName || row?.itemCode, "Chọn một serial khách trả mà máy này thay thế"].filter(Boolean).join(" · ")}
        closeButton={<CloseButton aria-label="Đóng" icon={<CloseIcon />} onClick={onClose} />}
      />
      <div style={{ margin: "0 24px", display: "flex", flexDirection: "column", gap: 8, maxHeight: 360, overflow: "auto" }}>
        {options.length === 0 ? (
          <p
            style={{
              margin: 0,
              padding: "18px 14px",
              border: "1px dashed #DDE0DA",
              borderRadius: 12,
              background: "#FAFAF8",
              color: "#5C6570",
              textAlign: "center",
            }}
          >
            Đơn không có serial khách trả, backend sẽ không ghi chuỗi đổi trả cho phiếu này.
          </p>
        ) : (
          options.map((item) => {
            const key = serialKey(item.serial);
            const holder = holderByOldSerial.get(key);
            const isReissued = reissued.has(key);
            const disabled = !!holder || isReissued;
            const reason = holder ? `Đã chọn cho ${holder}` : isReissued ? "Xuất lại trên phiếu này" : null;
            const otherItem = rowItemCode && serialKey(item.itemCode) !== rowItemCode;
            const checked = !disabled && key === selected;
            return (
              <label
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: checked ? 13 : 14,
                  border: checked ? "2px solid #1F5FAE" : `1px solid ${disabled ? "#E4E6E1" : "#D5D9D2"}`,
                  background: checked ? "#F2F7FD" : disabled ? "#FAFAF8" : "#FFFFFF",
                  color: disabled ? "#6B737C" : undefined,
                  borderRadius: 12,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                <input
                  type="radio"
                  name={radioName}
                  value={key}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => setSelected(key)}
                  style={{ width: 18, height: 18, margin: 0, flexShrink: 0, accentColor: "#1F5FAE" }}
                />
                <span style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{key}</span>
                  <span style={{ color: disabled ? undefined : "#5C6570", fontSize: 13 }}>
                    {[
                      item.itemName || item.itemCode,
                      item.sourceOrderNumber ? `đơn ${item.sourceOrderNumber}` : null,
                      otherItem ? "khác mã hàng" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                {reason && <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, textAlign: "right" }}>{reason}</span>}
              </label>
            );
          })
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "20px 24px 24px" }}>
        <DialogButton tone="secondary" onClick={() => currentKey && onConfirm(currentKey, [])}>
          Bỏ chọn
        </DialogButton>
        <DialogButton autoFocus disabled={!selected} onClick={() => currentKey && selected && onConfirm(currentKey, [selected])}>
          Xác nhận
        </DialogButton>
      </div>
    </Modal>
  );
};

export default ExchangeSerialPickerModal;
